import { createStore } from 'vuex'
import { AUTH_CONFIG } from '@/config'
import apiService from '@/services/api'

// Create a new store instance
export default createStore({
  state: {
    // Authentication
    token: localStorage.getItem(AUTH_CONFIG.TOKEN_KEY) || null,
    user: null,
    
    // UI state
    loading: false,
    error: null,
    sidebarOpen: true,
    
    // Data
    courses: [],
    currentCourse: null,
    lessons: [],
    currentLesson: null,
    assessmentQuestions: [],
    assessmentOptions: [],
    assessmentParts: [],
    homepageSections: [],
    users: [],
    blogPosts: [],
    banners: [],
    gptProcessing: false,
    gptResponse: null,
    walkthroughs: []
  },
  
  getters: {
    // Auth getters
    isAuthenticated: state => !!state.token,
    currentUser: state => state.user,
    
    // UI getters
    isLoading: state => state.loading,
    hasError: state => !!state.error,
    errorMessage: state => state.error,
    isSidebarOpen: state => state.sidebarOpen,
    
    // Data getters
    getCourses: state => state.courses,
    getCurrentCourse: state => state.currentCourse,
    getLessons: state => state.lessons,
    getCurrentLesson: state => state.currentLesson,
    getAssessmentQuestions: state => state.assessmentQuestions,
    getAssessmentOptions: state => state.assessmentOptions,
    getAssessmentParts: state => state.assessmentParts,
    getHomepageSections: state => state.homepageSections,
    getUsers: state => state.users,
    getBlogPosts: state => state.blogPosts,
    getBanners: state => state.banners,
    isGptProcessing: state => state.gptProcessing,
    getGptResponse: state => state.gptResponse,
    getWalkthroughs: state => state.walkthroughs
  },
  
  mutations: {
    // Auth mutations
    SET_TOKEN(state, token) {
      state.token = token
      if (token) {
        localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, token)
      } else {
        localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY)
      }
    },
    SET_USER(state, user) {
      state.user = user
    },
    
    // UI mutations
    SET_LOADING(state, isLoading) {
      state.loading = isLoading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    CLEAR_ERROR(state) {
      state.error = null
    },
    TOGGLE_SIDEBAR(state) {
      state.sidebarOpen = !state.sidebarOpen
    },
    SET_SIDEBAR(state, isOpen) {
      state.sidebarOpen = isOpen
    },
    
    // Data mutations
    SET_COURSES(state, courses) {
      state.courses = courses
    },
    SET_CURRENT_COURSE(state, course) {
      state.currentCourse = course
    },
    SET_LESSONS(state, lessons) {
      state.lessons = lessons
    },
    SET_CURRENT_LESSON(state, lesson) {
      state.currentLesson = lesson
    },
    SET_ASSESSMENT_QUESTIONS(state, questions) {
      state.assessmentQuestions = questions
    },
    SET_ASSESSMENT_OPTIONS(state, options) {
      state.assessmentOptions = options
    },
    SET_ASSESSMENT_PARTS(state, parts) {
      state.assessmentParts = parts
    },
    SET_HOMEPAGE_SECTIONS(state, sections) {
      state.homepageSections = sections
    },
    SET_USERS(state, users) {
      state.users = users
    },
    SET_BLOG_POSTS(state, posts) {
      state.blogPosts = posts
    },
    
    // Add/Update/Delete mutations
    ADD_COURSE(state, course) {
      state.courses.push(course)
    },
    UPDATE_COURSE(state, updatedCourse) {
      const index = state.courses.findIndex(c => c.id === updatedCourse.id)
      if (index !== -1) {
        state.courses.splice(index, 1, updatedCourse)
      }
    },
    DELETE_COURSE(state, courseId) {
      state.courses = state.courses.filter(c => c.id !== courseId)
    },
    
    ADD_LESSON(state, lesson) {
      state.lessons.push(lesson)
    },
    UPDATE_LESSON(state, updatedLesson) {
      const index = state.lessons.findIndex(l => l.id === updatedLesson.id)
      if (index !== -1) {
        state.lessons.splice(index, 1, updatedLesson)
      }
    },
    DELETE_LESSON(state, lessonId) {
      state.lessons = state.lessons.filter(l => l.id !== lessonId)
    },
    
    ADD_ASSESSMENT_QUESTION(state, question) {
      state.assessmentQuestions.push(question)
    },
    UPDATE_ASSESSMENT_QUESTION(state, updatedQuestion) {
      const index = state.assessmentQuestions.findIndex(q => q.id === updatedQuestion.id)
      if (index !== -1) {
        state.assessmentQuestions.splice(index, 1, updatedQuestion)
      }
    },
    DELETE_ASSESSMENT_QUESTION(state, questionId) {
      state.assessmentQuestions = state.assessmentQuestions.filter(q => q.id !== questionId)
    },
    
    ADD_ASSESSMENT_OPTION(state, option) {
      state.assessmentOptions.push(option)
    },
    UPDATE_ASSESSMENT_OPTION(state, updatedOption) {
      const index = state.assessmentOptions.findIndex(o => o.id === updatedOption.id)
      if (index !== -1) {
        state.assessmentOptions.splice(index, 1, updatedOption)
      }
    },
    DELETE_ASSESSMENT_OPTION(state, optionId) {
      state.assessmentOptions = state.assessmentOptions.filter(o => o.id !== optionId)
    },
    
    // Banner mutations
    SET_BANNERS(state, banners) {
      state.banners = banners
    },
    ADD_BANNER(state, banner) {
      state.banners.push(banner)
    },
    UPDATE_BANNER(state, updatedBanner) {
      const index = state.banners.findIndex(b => b.id === updatedBanner.id)
      if (index !== -1) {
        state.banners.splice(index, 1, updatedBanner)
      }
    },
    DELETE_BANNER(state, bannerId) {
      state.banners = state.banners.filter(b => b.id !== bannerId)
    },
    
    // GPT mutations
    SET_GPT_PROCESSING(state, isProcessing) {
      state.gptProcessing = isProcessing
    },
    SET_GPT_RESPONSE(state, response) {
      state.gptResponse = response
    },
    
    // Add this mutation
    SET_WALKTHROUGHS(state, walkthroughs) {
      state.walkthroughs = walkthroughs;
    }
  },
  
  actions: {
    // Auth actions
    async login({ commit }, credentials) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.auth.login(credentials)
        const { token, user } = response.data
        
        commit('SET_TOKEN', token)
        commit('SET_USER', user)
        
        return user
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Login failed')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async logout({ commit }) {
      try {
        commit('SET_LOADING', true)
        
        await apiService.auth.logout()
        
        commit('SET_TOKEN', null)
        commit('SET_USER', null)
        
        // Clear all data
        commit('SET_COURSES', [])
        commit('SET_CURRENT_COURSE', null)
        commit('SET_LESSONS', [])
        commit('SET_CURRENT_LESSON', null)
        commit('SET_ASSESSMENT_QUESTIONS', [])
        commit('SET_ASSESSMENT_OPTIONS', [])
        commit('SET_ASSESSMENT_PARTS', [])
        commit('SET_HOMEPAGE_SECTIONS', [])
        commit('SET_USERS', [])
        commit('SET_BLOG_POSTS', [])
        
      } catch (error) {
        console.error('Logout error:', error)
      } finally {
        // Even if the API call fails, we still want to clear the local state
        commit('SET_TOKEN', null)
        commit('SET_USER', null)
        commit('SET_LOADING', false)
      }
    },
    
    async fetchUserProfile({ commit }) {
      try {
        commit('SET_LOADING', true)
        
        const response = await apiService.auth.getProfile()
        commit('SET_USER', response.data)
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch user profile')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // Data actions
    async fetchCourses({ commit }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.courses.getAll()
        commit('SET_COURSES', response.data.data.courses)
        
        return response.data.data.courses
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch courses')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchCourse({ commit }, courseId) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.courses.get(courseId)
        commit('SET_CURRENT_COURSE', response.data.data)
        
        return response.data.data
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch course')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async createCourse({ commit }, courseData) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.courses.create(courseData)
        const newCourse = response.data.data
        
        commit('ADD_COURSE', newCourse)
        
        return newCourse
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to create course')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async updateCourse({ commit }, { courseId, courseData }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.courses.update(courseId, courseData)
        const updatedCourse = response.data.data
        
        commit('UPDATE_COURSE', updatedCourse)
        
        return updatedCourse
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to update course')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async deleteCourse({ commit }, courseId) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        await apiService.courses.delete(courseId)
        
        commit('DELETE_COURSE', courseId)
        
        return true
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to delete course')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // Assessment actions
    async fetchAssessmentQuestions({ commit }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.assessment.getQuestions()
        commit('SET_ASSESSMENT_QUESTIONS', response.data)
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch assessment questions')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchAssessmentOptions({ commit }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.assessment.getOptions()
        commit('SET_ASSESSMENT_OPTIONS', response.data)
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch assessment options')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchAssessmentParts({ commit }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.assessment.getParts()
        commit('SET_ASSESSMENT_PARTS', response.data)
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch assessment parts')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async createAssessmentOption({ commit }, optionData) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.assessment.createOption(optionData)
        const newOption = response.data
        
        commit('ADD_ASSESSMENT_OPTION', newOption)
        
        return newOption
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to create assessment option')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async updateAssessmentOption({ commit }, { optionId, optionData }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.assessment.updateOption(optionId, optionData)
        const updatedOption = response.data
        
        commit('UPDATE_ASSESSMENT_OPTION', updatedOption)
        
        return updatedOption
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to update assessment option')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async deleteAssessmentOption({ commit }, optionId) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        await apiService.assessment.deleteOption(optionId)
        
        commit('DELETE_ASSESSMENT_OPTION', optionId)
        
        return true
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to delete assessment option')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // Blog actions
    async fetchBlogPosts({ commit }, page = 1) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.blog.getPosts(page)
        commit('SET_BLOG_POSTS', response.data.data.data)
        
        return response.data.data
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch blog posts')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // Banner actions
    async fetchBanners({ commit }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.banners.getAll()
        commit('SET_BANNERS', response.data.data)
        
        return response.data.data
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to fetch banners')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async createBanner({ commit }, bannerData) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.banners.create(bannerData)
        commit('ADD_BANNER', response.data.data)
        
        return response.data.data
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to create banner')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async updateBanner({ commit }, { bannerId, bannerData }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.banners.update(bannerId, bannerData)
        commit('UPDATE_BANNER', response.data.data)
        
        return response.data.data
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to update banner')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async deleteBanner({ commit }, bannerId) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        await apiService.banners.delete(bannerId)
        commit('DELETE_BANNER', bannerId)
        
        return true
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to delete banner')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async toggleBannerActive({ commit, dispatch }, bannerId) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
        
        await apiService.banners.toggleActive(bannerId)
        await dispatch('fetchBanners')
        
        return true
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to update banner status')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // GPT actions
    async processBannerCommand({ commit, dispatch }, prompt) {
      try {
        commit('SET_GPT_PROCESSING', true)
        commit('CLEAR_ERROR')
        
        const response = await apiService.gpt.processBannerCommand(prompt)
        commit('SET_GPT_RESPONSE', response.data)
        
        // Handle GPT response actions
        if (response.data.action) {
          switch (response.data.action) {
            case 'create':
              if (response.data.bannerData) {
                await dispatch('createBanner', response.data.bannerData)
              }
              break
            case 'update':
              if (response.data.bannerId && response.data.bannerData) {
                await dispatch('updateBanner', {
                  bannerId: response.data.bannerId,
                  bannerData: response.data.bannerData
                })
              }
              break
            case 'delete':
              if (response.data.bannerId) {
                await dispatch('deleteBanner', response.data.bannerId)
              }
              break
            case 'toggle':
              if (response.data.bannerId) {
                await dispatch('toggleBannerActive', response.data.bannerId)
              }
              break
            case 'list':
              await dispatch('fetchBanners')
              break
          }
        }
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to process GPT command')
        throw error
      } finally {
        commit('SET_GPT_PROCESSING', false)
      }
    },
    
    // Add this action
    async fetchWalkthroughs({ commit }) {
      try {
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');
        
        const response = await apiService.walkthroughs.getAll();
        console.log('Walkthroughs API response:', response);
        console.log('Response data structure:', response.data);
        
        // Transform the object of arrays into a single array with category information
        const walkthroughsData = response.data.data || response.data;
        const flattenedWalkthroughs = [];
        
        // For each category in the response, add items to the flattened array
        Object.keys(walkthroughsData).forEach(category => {
          if (Array.isArray(walkthroughsData[category])) {
            walkthroughsData[category].forEach(item => {
              // Add the category to each item for reference
              flattenedWalkthroughs.push({
                ...item,
                category: category
              });
            });
          }
        });
        
        console.log('Flattened walkthroughs:', flattenedWalkthroughs);
        commit('SET_WALKTHROUGHS', flattenedWalkthroughs);
        
        return response.data;
      } catch (error) {
        console.error('Error fetching walkthroughs:', error);
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch walkthroughs');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    }
  },
  
  modules: {
    // Add modules here if needed
  }
}) 